// ContenidosListaSection.js — Sección "lista de contenidos" (Fase D)
// Renderiza los documentos FILTRADOS del usuario (rol/país/idioma vía documentos.php),
// con los botones de modo según modos_habilitados. Pensada para la página /contenidos.
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { consultaService, BACKEND_BASE } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useIdioma } from '../../../contexts/IdiomaContext';

const Wrap = styled.section` padding: 2rem 1rem; max-width: 1200px; margin: 0 auto; `;
const Titulo = styled.h2` text-align: center; color: #0f355b; margin: 0 0 0.4rem; `;
const Sub = styled.p` text-align: center; color: #64748b; margin: 0 0 1.5rem; `;
const Grid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 1.25rem;
`;
const Card = styled.div`
  background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; overflow: hidden;
  display: flex; flex-direction: column; box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  transition: transform 0.15s, box-shadow 0.15s;
  &:hover { transform: translateY(-3px); box-shadow: 0 8px 20px rgba(0,0,0,0.1); }
`;
const Img = styled.div`
  height: 150px; background: #f1f5f9 center/cover no-repeat;
  background-image: ${p => p.$src ? `url(${p.$src})` : 'none'};
  display: flex; align-items: center; justify-content: center; color: #cbd5e1; font-size: 2.5rem;
`;
const Body = styled.div` padding: 1rem; display: flex; flex-direction: column; flex: 1; `;
const CTitle = styled.h3` margin: 0 0 0.4rem; font-size: 1rem; color: #0f172a; `;
const CDesc = styled.p` margin: 0 0 0.9rem; font-size: 0.85rem; color: #64748b; flex: 1; `;
const Btns = styled.div` display: flex; gap: 0.5rem; flex-wrap: wrap; `;
const Btn = styled(Link)`
  background: ${p => p.$alt ? '#eef2f7' : '#0891b2'}; color: ${p => p.$alt ? '#0f355b' : '#fff'};
  text-decoration: none; padding: 0.45rem 0.8rem; border-radius: 8px; font-size: 0.82rem; font-weight: 600;
`;
const Badge = styled.span`
  display: inline-block; background: rgba(16,185,129,0.1); color: #059669;
  border-radius: 999px; padding: 1px 8px; font-size: 0.72rem; margin-right: 4px;
`;

const ContenidosListaSection = ({ config = {}, proyecto, tl }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useIdioma();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const modos = proyecto?.config_json?.modos_habilitados || {};
  const canAnalytics = user && (user.role === 'admin' || user.role === 'mentor');

  useEffect(() => {
    consultaService.getDocumentos()
      .then(r => setDocs(r.data || []))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, []);

  const titulo = tl ? tl(config.titulo, t('centro_contenidos')) : (config.titulo || 'Contenidos');
  const subtitulo = tl ? tl(config.subtitulo, t('selecciona_tema')) : (config.subtitulo || '');

  return (
    <Wrap>
      <Titulo>{titulo}</Titulo>
      {subtitulo && <Sub>{subtitulo}</Sub>}
      {loading ? (
        <p style={{ textAlign: 'center', color: '#94a3b8' }}>{t('cargando_contenidos')}</p>
      ) : docs.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#94a3b8' }}>{t('no_contenidos')}</p>
      ) : (
        <Grid>
          {docs.map(doc => (
            <Card key={doc.id}>
              <Img $src={doc.imagen ? `${BACKEND_BASE}/${doc.imagen}` : null}>{!doc.imagen && '📄'}</Img>
              <Body>
                <CTitle>{doc.titulo}</CTitle>
                <CDesc>{doc.descripcion || ''}</CDesc>
                <div style={{ marginBottom: '0.6rem' }}>
                  {doc.tiene_certificado ? <Badge>{t('certificado')}</Badge> : null}
                  {doc.idioma ? <Badge>{String(doc.idioma).toUpperCase()}</Badge> : null}
                </div>
                <Btns>
                  {modos.consulta !== false && <Btn to={`/consulta/${doc.id}`}>{t('consultar')}</Btn>}
                  {Number(doc.modo_evaluacion) === 1 && modos.evaluacion !== false &&
                    <Btn $alt to={`/consulta/${doc.id}?modo=evaluacion`}>{t('evaluacion') || 'Evaluación'}</Btn>}
                  {Number(doc.modo_mentor) === 1 && modos.mentor !== false &&
                    <Btn $alt to={`/mentor/${doc.id}`}>{t('mentor')}</Btn>}
                  {canAnalytics &&
                    <Btn $alt as="button" onClick={() => navigate(`/analytics/${doc.id}`)} style={{ border: 'none', cursor: 'pointer' }}>📊</Btn>}
                </Btns>
              </Body>
            </Card>
          ))}
        </Grid>
      )}
    </Wrap>
  );
};

export default ContenidosListaSection;
